// ── COACHING: the parent side ────────────────────────────────────────
// One screen for the whole thing. A family states preferences — which coaches
// they'd be happy with, which hours they're free — and Lori assigns the actual
// times, so nothing here books anything and nothing here is charged. The
// request form is a modal over this page rather than its own view: it's four
// questions, the performer is already decided by which row you opened it from,
// and doing a second child is one more click instead of a round trip.
//
// Confirming is the one place money moves: the card on file is charged for
// everything the family confirms at once. There is deliberately no way to
// decline a single time here — that's a phone call to Lori, who moves them on
// the board. A button that files a rejection would just make her a queue.
import { apiFetch } from '../core/api.js';
import { COACHING_MINE, setCoachingMine, DB, session } from '../core/state.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';

const DAY_FMT = { weekday: 'short', month: 'short', day: 'numeric' };
const fmtDay = iso => new Date(iso + 'T00:00').toLocaleDateString('en-US', DAY_FMT);

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')}${suffix}`;
}

// PerformerRoutes hands ids back as strings and marks archiving with
// archivedAt, so normalise once here rather than string-vs-number comparing
// against the numeric ids every coaching row and request carries.
function myPerformers() {
  return (DB.children || [])
    .filter(c => !c.archivedAt && String(c.accountId) === String(session.id))
    .map(c => ({ ...c, id: Number(c.id) }));
}

function assignmentsFor(eventId) {
  return (COACHING_MINE.assignments || []).filter(a => a.eventId === eventId);
}

function performerName(performerId) {
  const p = myPerformers().find(x => x.id === performerId);
  return p ? `${p.firstName} ${p.lastName}` : '';
}

function requestFor(eventId, performerId) {
  return (COACHING_MINE.requests || []).find(
    r => r.eventId === eventId && r.performerId === performerId
  );
}

// ── The page ─────────────────────────────────────────────────────────

function renderPerformerRow(ev, performer) {
  const req = requestFor(ev.id, performer.id);
  const name = `${performer.firstName} ${performer.lastName}`;

  if (!req) {
    return `<div class="coaching-row">
      <span style="font-size:15px;flex-grow:1">${esc(name)}</span>
      ${ev.open
        ? `<button class="btn-ghost btn-sm" style="border-color:rgba(201,168,76,0.5);color:var(--gold-light)" data-action="pc-request" data-event="${ev.id}" data-performer="${performer.id}">Request coaching</button>`
        : '<span style="font-size:13px;color:var(--muted)">No request</span>'}
    </div>`;
  }

  const coachNames = (ev.coaches || [])
    .filter(c => req.coachIds.includes(c.id))
    .map(c => c.name);
  const coachLabel = coachNames.length === (ev.coaches || []).length
    ? 'any coach'
    : coachNames.join(' or ');
  const windowLabel = (ev.windows || [])
    .filter(w => req.windows.includes(w.hour))
    .map(w => w.label)
    .join(', ');

  return `<div class="coaching-row">
    <span style="font-size:15px;width:132px;flex-shrink:0">${esc(name)}</span>
    <div style="flex-grow:1;min-width:220px">
      <div style="font-size:14px">Requested ${req.sessions} ${req.sessions === 1 ? 'session' : 'sessions'} — ${esc(coachLabel)}, free ${esc(windowLabel)}</div>
      <div style="font-size:12.5px;color:var(--muted)">${req.locked ? 'A time has been set aside — we\'ll email you' : 'Lori is still building the schedule'}</div>
    </div>
    ${ev.open && !req.locked
      ? `<button class="btn-ghost btn-sm" data-action="pc-request" data-event="${ev.id}" data-performer="${performer.id}">Change</button>`
      : ''}
  </div>`;
}

function statusPill(ev) {
  const pending = assignmentsFor(ev.id).filter(a => a.status === 'sent').length;
  if (pending) return '<span class="pill pill-amber">Needs your confirmation</span>';
  if (assignmentsFor(ev.id).length) return '<span class="pill pill-active">Confirmed &amp; paid</span>';
  if (ev.open) {
    return ev.requestsCloseAt
      ? `<span class="pill pill-gold">Requests open until ${fmtDay(ev.requestsCloseAt)}</span>`
      : '<span class="pill pill-gold">Requests open</span>';
  }
  const today = new Date().toISOString().slice(0, 10);
  if (today < ev.requestsOpenAt) {
    return `<span class="pill pill-off">Requests open ${fmtDay(ev.requestsOpenAt)}</span>`;
  }
  return '<span class="pill pill-off">Requests closed</span>';
}

function renderAssigned(ev) {
  const rows = assignmentsFor(ev.id);
  if (!rows.length) return '';

  const pending = rows.filter(a => a.status === 'sent');
  const lines = rows.map(a => {
    const paid = a.status === 'confirmed';
    return `<div class="coaching-row" style="border-color:${paid ? 'rgba(52,211,153,0.25)' : 'rgba(201,168,76,0.18)'}">
      <span style="font-size:15px;width:132px;flex-shrink:0">${esc(performerName(a.performerId))}</span>
      <div style="flex-grow:1;min-width:210px">
        <div class="serif" style="font-size:18px;color:${paid ? 'var(--cream)' : 'var(--gold-light)'}">${fmtTime(a.startsAt)} with ${esc(a.coachName)}</div>
        <div style="font-size:12.5px;color:var(--muted)">${esc(ev.name)} · ${ev.slotMinutes} minutes · ${fmt$(ev.price)}</div>
      </div>
      <span class="pill ${paid ? 'pill-active' : 'pill-amber'}">${paid ? 'Confirmed &amp; paid' : 'Not confirmed'}</span>
    </div>`;
  }).join('');

  const total = pending.length * ev.price;
  const footer = pending.length ? `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-top:18px;padding-top:16px;border-top:1px solid var(--border-soft)">
      <div style="font-size:13px;color:var(--muted)">Confirming charges <b class="amt">${fmt$(total)}</b> to the card on file.</div>
      <button class="btn" style="width:auto;padding:10px 22px" data-action="pc-confirm" data-event="${ev.id}">Confirm ${pending.length === 1 ? 'this time' : 'these ' + pending.length + ' times'} &amp; pay ${fmt$(total)}</button>
    </div>
    <div style="font-size:12.5px;color:var(--muted);margin-top:12px;line-height:1.6">If one of these times doesn't work, give Lori a call or reply to your email and she'll sort it out with you.</div>` : '';

  return `<div style="display:flex;flex-direction:column;gap:10px;padding-top:16px;border-top:1px solid var(--border-soft)">${lines}</div>${footer}`;
}

function renderEventCard(ev) {
  const performers = myPerformers();
  const notYetOpen = !ev.open && new Date().toISOString().slice(0, 10) < ev.requestsOpenAt;
  const assigned = renderAssigned(ev);

  return `<div class="card">
    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:20px;flex-wrap:wrap;margin-bottom:18px">
      <div>
        <div class="serif" style="font-size:20px">${esc(ev.name)}</div>
        <div style="font-size:13.5px;color:var(--muted);margin-top:3px">${fmtDay(ev.eventDate)} · ${fmtTime(ev.startsAt)}–${fmtTime(ev.endsAt)} · ${fmt$(ev.price)} per ${ev.slotMinutes}-minute session</div>
      </div>
      ${statusPill(ev)}
    </div>
    ${assigned || (notYetOpen
      ? '<div style="font-size:13px;color:var(--muted);padding-top:16px;border-top:1px solid var(--border-soft)">We\'ll email you when this one opens.</div>'
      : `<div style="display:flex;flex-direction:column;gap:10px;padding-top:16px;border-top:1px solid var(--border-soft)">
          ${performers.map(p => renderPerformerRow(ev, p)).join('')}
        </div>`)}
  </div>`;
}

VIEWS['p-coaching'] = () => {
  const events = COACHING_MINE.events || [];
  const head = `<div class="page-head">
    <span class="eyebrow">Coaching</span>
    <h2 class="serif">One-on-one <em>coaching</em></h2>
    <div class="sub">Twenty focused minutes with one of our coaches, before audition season.</div>
  </div>`;

  if (!events.length) {
    return `${head}<div class="card"><p class="empty">No coaching days are open right now.</p></div>`;
  }

  const anyRequests = (COACHING_MINE.requests || []).length > 0;
  // The three steps only earn their place before a family has done this once —
  // after that the page speaks for itself.
  const steps = anyRequests ? '' : `<div class="grid-3" style="margin-bottom:24px">
    ${[
      ['1', 'You tell us what works.', "Which coaches, and which hours you're free that day."],
      ['2', 'Lori builds the schedule.', 'She fits everyone together and emails you a time.'],
      ['3', 'You confirm.', "Your card is charged only once you've said the time works."]
    ].map(([n, title, body]) => `<div style="display:flex;gap:12px;align-items:flex-start">
      <span style="font-family:'Libre Baskerville',serif;font-size:15px;color:var(--gold);width:26px;height:26px;border:1px solid rgba(201,168,76,0.35);border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0">${n}</span>
      <div style="font-size:13px;color:var(--muted);line-height:1.6"><b style="color:var(--cream);font-weight:400">${title}</b> ${body}</div>
    </div>`).join('')}
  </div>`;

  return `${head}${steps}
    <div style="display:flex;flex-direction:column;gap:18px">${events.map(renderEventCard).join('')}</div>
    <p style="font-size:12.5px;color:var(--muted);line-height:1.7;max-width:60ch;margin-top:18px">
      Each performer gets their own request, up to ${events[0].maxSessions} sessions each. You're telling us preferences, not booking a time — the more coaches and hours you can say yes to, the more likely we can fit you in.
    </p>`;
};

function wireParentCoaching() {
  document.querySelectorAll('[data-action="pc-request"]').forEach(btn => {
    btn.addEventListener('click', () => requestModal(Number(btn.dataset.event), Number(btn.dataset.performer)));
  });
  document.querySelectorAll('[data-action="pc-confirm"]').forEach(btn => {
    btn.addEventListener('click', () => confirmTimes(Number(btn.dataset.event), btn));
  });
}

async function confirmTimes(eventId, btn) {
  btn.disabled = true;
  btn.style.opacity = '0.5';
  try {
    await apiFetch('/coaching/confirm', { method: 'POST', body: JSON.stringify({ eventId }) });
    // Re-read rather than patching: the charge, the statuses and the card all
    // moved server-side, and this page is the receipt for all three.
    setCoachingMine(await apiFetch('/coaching/my'));
    document.getElementById('main').innerHTML = VIEWS['p-coaching']();
    wireParentCoaching();
    toast('Confirmed — see you there!');
  } catch (e) {
    btn.disabled = false;
    btn.style.opacity = '1';
    toastError(e.message);
  }
}

VIEW_INIT['p-coaching'] = wireParentCoaching;

// ── The request modal ────────────────────────────────────────────────

let rmState = null; // { eventId, performerId, sessions, coaches:Set, windows:Set }

function requestModal(eventId, performerId) {
  const ev = (COACHING_MINE.events || []).find(e => e.id === eventId);
  const performer = myPerformers().find(p => p.id === performerId);
  if (!ev || !performer) return;

  const existing = requestFor(eventId, performerId);
  rmState = {
    eventId,
    performerId,
    // One session is the common case; two is the exception a family opts into.
    sessions: existing ? existing.sessions : 1,
    // Nothing is pre-picked: every coach and hour here should be a deliberate
    // yes, because each one is a promise the schedule gets held to.
    coaches: new Set(existing ? existing.coachIds : []),
    windows: new Set(existing ? existing.windows : [])
  };

  openModal(`
    <h3 class="serif">${existing ? 'Change request' : 'Request coaching'}</h3>
    <div style="font-size:13.5px;color:var(--muted);margin-bottom:22px">${esc(performer.firstName + ' ' + performer.lastName)} · ${esc(ev.name)}, ${fmtDay(ev.eventDate)}</div>
    <div id="rmBody"></div>
    <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;padding-top:18px;border-top:1px solid var(--border-soft)">
      <div id="rmHint" style="font-size:12.5px;max-width:260px;line-height:1.6"></div>
      <div style="display:flex;gap:10px">
        <button class="btn-ghost" onclick="closeModal()">Cancel</button>
        <button class="btn" id="rmSave" style="width:auto;padding:10px 22px">${existing ? 'Save changes' : 'Send request'}</button>
      </div>
    </div>`);

  renderModalBody(ev);
  document.getElementById('rmSave').addEventListener('click', () => saveRequest(ev));
}

function chip(label, on, action, value) {
  return `<button data-action="${action}" data-value="${value}" style="border:1px solid ${on ? 'var(--gold)' : 'var(--border-soft)'};background:${on ? 'rgba(201,168,76,0.12)' : 'var(--bg-input)'};color:${on ? 'var(--gold-light)' : 'var(--cream)'};border-radius:7px;padding:9px 14px;font-size:14px;font-family:inherit;cursor:pointer;text-align:center;width:100%">${label}</button>`;
}

function renderModalBody(ev) {
  const s = rmState;
  const allCoaches = ev.coaches || [];
  const anyPicked = allCoaches.length > 0 && s.coaches.size === allCoaches.length;
  const grid = inner => `<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px">${inner}</div>`;

  const sessionChips = [];
  for (let n = 1; n <= ev.maxSessions; n++) {
    sessionChips.push(chip(`${n === 1 ? 'One session' : n === 2 ? 'Two sessions' : n + ' sessions'} · ${fmt$(ev.price * n)}`, s.sessions === n, 'rm-sessions', n));
  }

  document.getElementById('rmBody').innerHTML = `
    <div style="margin-bottom:20px">
      <label style="display:block;margin-bottom:8px">How many sessions?</label>
      ${grid(sessionChips.join(''))}
    </div>
    <div style="margin-bottom:20px">
      <label style="display:block;margin-bottom:8px">Which coaches would work?</label>
      <div style="margin-bottom:10px">${chip('Any coach is fine', anyPicked, 'rm-any', 'any')}</div>
      ${grid(allCoaches.map(c => chip(esc(c.name), s.coaches.has(c.id), 'rm-coach', c.id)).join(''))}
    </div>
    <div style="margin-bottom:22px">
      <label style="display:block;margin-bottom:8px">When are you free that day?</label>
      ${grid((ev.windows || []).map(w => chip(w.label, s.windows.has(w.hour), 'rm-window', w.hour)).join(''))}
    </div>`;

  const ready = s.coaches.size > 0 && s.windows.size > 0;
  const hint = document.getElementById('rmHint');
  hint.textContent = ready ? '' : (s.coaches.size === 0
    ? "Choose at least one coach you'd be happy with."
    : 'Choose at least one hour you could make it.');
  hint.style.color = 'var(--pill-amber, #fbbf24)';

  const save = document.getElementById('rmSave');
  save.disabled = !ready;
  save.style.opacity = ready ? '1' : '0.45';
  save.style.cursor = ready ? 'pointer' : 'not-allowed';

  document.getElementById('rmBody').querySelectorAll('[data-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      const v = btn.dataset.value;
      if (btn.dataset.action === 'rm-sessions') rmState.sessions = Number(v);
      if (btn.dataset.action === 'rm-coach') toggle(rmState.coaches, Number(v));
      if (btn.dataset.action === 'rm-window') toggle(rmState.windows, Number(v));
      if (btn.dataset.action === 'rm-any') {
        if (anyPicked) rmState.coaches.clear();
        else allCoaches.forEach(c => rmState.coaches.add(c.id));
      }
      renderModalBody(ev);
    });
  });
}

function toggle(set, v) {
  if (set.has(v)) set.delete(v);
  else set.add(v);
}

async function saveRequest(ev) {
  const s = rmState;
  if (!s.coaches.size || !s.windows.size) return;

  try {
    const saved = await apiFetch('/coaching/requests', {
      method: 'POST',
      body: JSON.stringify({
        eventId: s.eventId,
        performerId: s.performerId,
        sessions: s.sessions,
        coachIds: [...s.coaches],
        windows: [...s.windows]
      })
    });
    const requests = COACHING_MINE.requests || [];
    const i = requests.findIndex(r => r.eventId === saved.eventId && r.performerId === saved.performerId);
    if (i >= 0) requests[i] = saved; else requests.push(saved);
    setCoachingMine({ ...COACHING_MINE, requests });
    closeModal();
    document.getElementById('main').innerHTML = VIEWS['p-coaching']();
    wireParentCoaching();
    toast('Request sent — we\'ll email you once the schedule is set');
  } catch (e) {
    toastError(e.message);
  }
}
